import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAddressAutofillCore } from '@mapbox/search-js-react';
import { SessionToken } from '@mapbox/search-js-core';
import type { AddressAutofillSuggestion } from '@mapbox/search-js-core';
import './address-autocomplete.scss';

const ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

export interface MapboxData {
  mapbox_id: string;
  full_address: string;
  accuracy: string;
  place_name: string;
}

export interface AddressFields {
  street_address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  mapbox_data: MapboxData | null;
}

interface AddressAutocompleteProps {
  onPlaceSelected: (address: AddressFields) => void;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onPlaceSelected,
  value,
  onChange,
  placeholder = 'Search address...',
  disabled = false,
  className = '',
}) => {
  const [suggestions, setSuggestions] = useState<AddressAutofillSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const sessionTokenRef = useRef(new SessionToken());
  const wrapperRef = useRef<HTMLDivElement>(null);

  const autofill = useAddressAutofillCore({
    accessToken: ACCESS_TOKEN,
    country: 'AU',
  });

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    try {
      const response = await autofill.suggest(query, {
        sessionToken: sessionTokenRef.current,
      });
      setSuggestions(response.suggestions);
      setShowDropdown(response.suggestions.length > 0);
      setActiveIndex(-1);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [autofill]);

  const handleSelect = useCallback(async (suggestion: AddressAutofillSuggestion) => {
    setShowDropdown(false);
    setSuggestions([]);

    try {
      const result = await autofill.retrieve(suggestion, {
        sessionToken: sessionTokenRef.current,
      });
      sessionTokenRef.current = new SessionToken();

      const feature = result.features[0];
      if (!feature) return;

      const props = feature.properties;
      const [lng, lat] = feature.geometry.coordinates;

      onPlaceSelected({
        street_address: props.address_line1 || '',
        city: props.address_level2 || '',
        state: props.address_level1 || '',
        postcode: props.postcode || '',
        country: props.country || 'Australia',
        latitude: lat ?? null,
        longitude: lng ?? null,
        mapbox_data: {
          mapbox_id: props.mapbox_id || '',
          full_address: props.full_address || '',
          accuracy: props.accuracy || '',
          place_name: props.place_name || props.feature_name || '',
        },
      });
    } catch (err) {
      console.error('Error retrieving address:', err);
    }
  }, [autofill, onPlaceSelected]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange?.(val);
    fetchSuggestions(val);
  }, [onChange, fetchSuggestions]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, [showDropdown, suggestions, activeIndex, handleSelect]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!ACCESS_TOKEN) {
    return (
      <input
        type="text"
        className={`address-search-input ${className}`}
        placeholder="Address search unavailable"
        disabled
      />
    );
  }

  return (
    <div className="address-autocomplete" ref={wrapperRef}>
      <input
        type="text"
        className={`address-search-input ${className}`}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="address-suggestions-listbox"
        aria-autocomplete="list"
        autoComplete="off"
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="address-suggestions" role="listbox" id="address-suggestions-listbox">
          {suggestions.map((s, i) => (
            <li
              key={s.mapbox_id || i}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-suggestion ${i === activeIndex ? 'active' : ''}`}
              onMouseDown={(e) => { e.stopPropagation(); handleSelect(s); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="suggestion-primary">
                {s.address_line1 || s.feature_name}
              </span>
              {s.full_address && s.full_address !== s.address_line1 && (
                <span className="suggestion-secondary">
                  {s.full_address}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
